import React, { useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Typography, Box, Avatar, Chip, IconButton, TextField, InputAdornment, Tooltip,
} from '@mui/material';
import {
  Close as CloseIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import { computeAutoTeam } from '../utils/players';
import { SKILL_LABELS } from '../constants';

export default function PlayerDetailDialog({ player, open, onClose, currentUserRole, onEdit, onDelete, onEmailChange, isOwnProfile, mode = 'default' }) {
  const [editingEmail, setEditingEmail] = useState(false);
  const [emailDraft, setEmailDraft] = useState('');
  const [emailLoading, setEmailLoading] = useState(false);
  const [emailError, setEmailError] = useState('');

  const canEditEmail = onEmailChange && (currentUserRole === 'admin' || isOwnProfile);

  const handleEmailSave = async () => {
    if (!emailDraft || emailDraft === player.email) { setEditingEmail(false); return; }
    setEmailLoading(true); setEmailError('');
    const err = await onEmailChange(emailDraft);
    setEmailLoading(false);
    if (err) { setEmailError(err); } else { setEditingEmail(false); }
  };

  if (!player) return null;
  const isPrivileged = currentUserRole === 'admin' || currentUserRole === 'trainer';
  const isLineupMode = mode === 'lineup';
  const autoTeams = computeAutoTeam(player.birthyear, player.gender);
  const InfoRow = ({ label, value }) => value ? (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
      py: 1, borderBottom: '1px solid #f0f0f0' }}>
      <Typography variant="body2" color="text.secondary" sx={{ minWidth: 130, flexShrink: 0 }}>{label}</Typography>
      <Typography variant="body2" fontWeight={600} textAlign="right" sx={{ ml: 1, wordBreak: 'break-word' }}>{value}</Typography>
    </Box>
  ) : null;
  const addressStr = [player.street, player.streetNumber, player.cityCode, player.city].filter(Boolean).join(' ') || null;
  const favoritePositions = [
    player.primaryPosition ? { label: '1. Position', value: player.primaryPosition, color: '#6A1B9A', bg: '#F3E5F5' } : null,
    (isLineupMode || isPrivileged) && player.secondaryPosition ? { label: '2. Position', value: player.secondaryPosition, color: '#E65100', bg: '#FFF3E0' } : null,
  ].filter(Boolean);
  const skillEntries = SKILL_LABELS
    .map(({ key, label }) => ({ label, value: Number(player.skillStats?.[key]) }))
    .filter(({ value }) => Number.isFinite(value));
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Avatar sx={{ bgcolor: '#0D47A1', width: 48, height: 48, fontWeight: 700, fontSize: 17 }}>
            {player.firstName?.[0]}{player.lastName?.[0]}
          </Avatar>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography fontWeight={700} fontSize={17}>
              {player.firstName} {player.lastName}
              {!isLineupMode && player.nickname ? <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>„{player.nickname}"</Typography> : null}
            </Typography>
            {!isLineupMode && (
              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.25 }}>
                {player.team && <Chip size="small" label={player.team} sx={{ height: 18, fontSize: 11 }} />}
                {autoTeams.map(t => <Chip key={t} size="small" label={t} sx={{ height: 18, fontSize: 11, bgcolor: '#E3F2FD', color: '#0D47A1' }} />)}
              </Box>
            )}
          </Box>
        </Box>
        <IconButton onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8 }}><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent sx={{ pt: '8px !important' }}>
        {isLineupMode ? (
          <>
            <Typography variant="overline" color="text.secondary" sx={{ display: 'block', mb: 1 }}>Favorisierte Positionen</Typography>
            <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mb: 2.5 }}>
              {favoritePositions.length > 0 ? favoritePositions.map((position) => (
                <Box key={position.label} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', bgcolor: position.bg, borderRadius: 2, px: 2, py: 1, minWidth: 88, textAlign: 'center' }}>
                  <Typography variant="h6" fontWeight={700} color={position.color} sx={{ lineHeight: 1.2 }}>{position.value}</Typography>
                  <Typography variant="caption" color="text.secondary">{position.label}</Typography>
                </Box>
              )) : (
                <Typography variant="body2" color="text.secondary">Keine bevorzugten Positionen hinterlegt.</Typography>
              )}
            </Box>
            <Typography variant="overline" color="text.secondary" sx={{ display: 'block', mb: 1 }}>Skillbewertung</Typography>
            {skillEntries.length > 0 ? (
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', sm: '1fr 1fr 1fr' }, gap: 1 }}>
                {skillEntries.map(({ label, value }) => (
                  <Box key={label} sx={{ border: '1px solid #dce6f2', borderRadius: 2, px: 1.25, py: 1, bgcolor: '#F8FBFF' }}>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.25 }}>{label}</Typography>
                    <Typography variant="body1" fontWeight={700}>{value}</Typography>
                  </Box>
                ))}
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary">Keine Skillwerte hinterlegt.</Typography>
            )}
          </>
        ) : (
          <>
            <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mb: 2 }}>
              {player.capNumber && (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center',
                  bgcolor: '#E3F2FD', borderRadius: 2, px: 2, py: 1, minWidth: 54, textAlign: 'center' }}>
                  <Typography variant="h4" fontWeight={900} color="primary" sx={{ lineHeight: 1 }}>#{player.capNumber}</Typography>
                  <Typography variant="caption" color="text.secondary">Kappe</Typography>
                </Box>
              )}
              {favoritePositions.map((position) => (
                <Box key={position.label} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', bgcolor: position.bg, borderRadius: 2, px: 2, py: 1, minWidth: 54, textAlign: 'center' }}>
                  <Typography variant="h6" fontWeight={700} color={position.color} sx={{ lineHeight: 1.2 }}>{position.value}</Typography>
                  <Typography variant="caption" color="text.secondary">{position.label}</Typography>
                </Box>
              ))}
              {isPrivileged && player.isReferee && (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center',
                  bgcolor: '#E8F5E9', borderRadius: 2, px: 2, py: 1, minWidth: 54, textAlign: 'center' }}>
                  <Typography variant="body1" fontWeight={700} color="#2E7D32" sx={{ lineHeight: 1.2 }}>✓</Typography>
                  <Typography variant="caption" color="text.secondary">Schiedsrichter</Typography>
                </Box>
              )}
            </Box>
            {isPrivileged && player.email && !canEditEmail && <InfoRow label="E-Mail" value={player.email} />}
            {player.email && canEditEmail && !editingEmail && (
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            py: 1, borderBottom: '1px solid #f0f0f0' }}>
            <Typography variant="body2" color="text.secondary" sx={{ minWidth: 130, flexShrink: 0 }}>E-Mail</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: 1 }}>
              <Typography variant="body2" fontWeight={600} sx={{ wordBreak: 'break-word', textAlign: 'right' }}>{player.email}</Typography>
              <Tooltip title="E-Mail ändern">
                <IconButton size="small" onClick={() => { setEmailDraft(player.email); setEditingEmail(true); setEmailError(''); }}
                  sx={{ color: '#0D47A1', flexShrink: 0 }}><EditIcon fontSize="small" /></IconButton>
              </Tooltip>
            </Box>
          </Box>
            )}
            {canEditEmail && editingEmail && (
          <Box sx={{ py: 1, borderBottom: '1px solid #f0f0f0' }}>
            <TextField label="E-Mail" type="email" size="small" fullWidth autoFocus
              value={emailDraft} onChange={e => setEmailDraft(e.target.value)}
              error={!!emailError} helperText={emailError || 'Wird auch als Login-E-Mail verwendet'}
              onKeyDown={e => { if (e.key === 'Enter') handleEmailSave(); if (e.key === 'Escape') setEditingEmail(false); }}
              InputProps={{ endAdornment: (
                <InputAdornment position="end">
                  <IconButton size="small" disabled={emailLoading} onClick={handleEmailSave}
                    sx={{ color: '#2E7D32' }}><CheckCircleIcon fontSize="small" /></IconButton>
                  <IconButton size="small" onClick={() => setEditingEmail(false)}
                    sx={{ color: '#C62828' }}><CloseIcon fontSize="small" /></IconButton>
                </InputAdornment>
              ) }} />
          </Box>
            )}
            {!player.email && canEditEmail && (
          <Box sx={{ py: 1, borderBottom: '1px solid #f0f0f0' }}>
            <Button size="small" startIcon={<EditIcon />} onClick={() => { setEmailDraft(''); setEditingEmail(true); setEmailError(''); }}
              sx={{ textTransform: 'none', color: '#0D47A1' }}>E-Mail hinzufügen</Button>
          </Box>
            )}
            {addressStr && <InfoRow label="Adresse" value={addressStr} />}
            {player.playerPassNumber && <InfoRow label="Spielerpass-Nr." value={player.playerPassNumber} />}
            {player.isReferee && player.refereePassNumber && <InfoRow label="Schiedsrichterpass-Nr." value={player.refereePassNumber} />}
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        {(onEdit || onDelete) && (
          <Box sx={{ flex: 1, display: 'flex', gap: 0.5 }}>
            {onEdit && <Tooltip title="Skills und Positionen bearbeiten"><IconButton size="small" onClick={() => onEdit(player)} sx={{ color: '#0D47A1' }}><EditIcon /></IconButton></Tooltip>}
            {!isLineupMode && onDelete && <Tooltip title="Löschen"><IconButton size="small" onClick={() => onDelete(player)} sx={{ color: '#C62828' }}><DeleteIcon /></IconButton></Tooltip>}
          </Box>
        )}
        <Button onClick={onClose} color="inherit">Schließen</Button>
      </DialogActions>
    </Dialog>
  );
}
